/**
 * Alshallal Pastry Company HR System - AI Inbox Page
 * Phase 23: In-App AI Copilot with Human-in-the-Loop
 * 
 * HR admin view for managing AI-initiated requests
 * Access: Super Admin, HR Manager, HR Admin
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Bot,
  Inbox,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  HelpCircle,
  Clock,
  User,
  MessageSquare,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  FileText
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configuration
const STATUS_CONFIG = {
  pending_hr: { label: { en: 'Pending', ar: 'قيد الانتظار' }, color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  queued_human: { label: { en: 'Queued', ar: 'في قائمة الانتظار' }, color: 'bg-orange-100 text-orange-800', icon: Inbox },
  approved: { label: { en: 'Approved', ar: 'موافق عليه' }, color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: { en: 'Rejected', ar: 'مرفوض' }, color: 'bg-red-100 text-red-800', icon: XCircle },
  needs_info: { label: { en: 'Needs Info', ar: 'يحتاج معلومات' }, color: 'bg-blue-100 text-blue-800', icon: HelpCircle },
};

// Request type configuration
const REQUEST_TYPES = {
  leave_request: { en: 'Leave Request', ar: 'طلب إجازة' },
  salary_certificate: { en: 'Salary Certificate', ar: 'شهادة راتب' },
  document_inquiry: { en: 'Document Inquiry', ar: 'استفسار عن مستند' },
  general_inquiry: { en: 'General Inquiry', ar: 'استفسار عام' },
  equipment_request: { en: 'Equipment Request', ar: 'طلب معدات' },
  id_renewal: { en: 'ID Renewal', ar: 'تجديد الهوية' },
  quota_exceeded: { en: 'Quota Exceeded', ar: 'تجاوز الحصة' },
  other: { en: 'Other', ar: 'أخرى' },
};

const AIInboxPage = () => {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  
  // Selected request for detail view
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Fetch requests
  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('request_type', typeFilter);
      params.append('page', page);
      params.append('page_size', pageSize);
      
      const response = await axios.get(
        `${API_URL}/ai/admin/requests?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/ai/admin/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };
  
  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [statusFilter, typeFilter, page]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle action on request
  const handleAction = async (action) => {
    if (!selectedRequest) return;
    
    // Validate rejection reason
    if (action === 'reject' && (!rejectionReason || rejectionReason.length < 10)) {
      setError(isRTL ? 'سبب الرفض مطلوب (10 أحرف على الأقل)' : 'Rejection reason is required (min 10 characters)');
      return;
    }
    
    setActionLoading(true);
    setError(null);
    
    try {
      await axios.patch(
        `${API_URL}/ai/admin/requests/${selectedRequest.id}`,
        {
          action,
          notes: actionNotes || undefined,
          rejection_reason: action === 'reject' ? rejectionReason : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh list
      await fetchRequests();
      await fetchStats();
      
      // Close panel and reset
      setSelectedRequest(null);
      setActionNotes('');
      setRejectionReason('');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process action');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Filter displayed requests by search
  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.employee_name?.toLowerCase().includes(query) ||
      req.ai_summary?.toLowerCase().includes(query) ||
      req.original_message?.toLowerCase().includes(query)
    );
  });
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-purple-600" />
            {isRTL ? 'صندوق الذكاء الاصطناعي' : 'AI Inbox'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isRTL 
              ? 'مراجعة الطلبات المنشأة بواسطة مساعد الذكاء الاصطناعي'
              : 'Review requests created by the AI assistant'}
          </p>
        </div>
        
        <Button onClick={() => { fetchRequests(); fetchStats(); }} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.requests?.pending_hr || 0}</p>
                  <p className="text-xs text-slate-500">{isRTL ? 'قيد الانتظار' : 'Pending'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.requests?.approved || 0}</p>
                  <p className="text-xs text-slate-500">{isRTL ? 'موافق عليها' : 'Approved'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.requests?.rejected || 0}</p>
                  <p className="text-xs text-slate-500">{isRTL ? 'مرفوضة' : 'Rejected'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.conversations?.total || 0}</p>
                  <p className="text-xs text-slate-500">{isRTL ? 'المحادثات' : 'Conversations'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={isRTL ? 'بحث...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder={isRTL ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {isRTL ? config.label.ar : config.label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder={isRTL ? 'النوع' : 'Type'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'جميع الأنواع' : 'All Types'}</SelectItem>
                {Object.entries(REQUEST_TYPES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {isRTL ? label.ar : label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-purple-500" />
              <p className="text-sm text-slate-500 mt-2">
                {isRTL ? 'جاري التحميل...' : 'Loading...'}
              </p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-medium text-slate-600">
                {isRTL ? 'لا توجد طلبات' : 'No requests found'}
              </h3>
              <p className="text-sm text-slate-500">
                {isRTL 
                  ? 'ستظهر الطلبات المنشأة بواسطة الذكاء الاصطناعي هنا'
                  : 'AI-created requests will appear here'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                  <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{isRTL ? 'الملخص' : 'Summary'}</TableHead>
                  <TableHead>{isRTL ? 'اللغة' : 'Language'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead>{isRTL ? 'التاريخ' : 'Date'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((req) => {
                  const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending_hr;
                  const StatusIcon = statusConfig.icon;
                  const typeLabel = REQUEST_TYPES[req.request_type] || REQUEST_TYPES.other;
                  
                  return (
                    <TableRow key={req.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedRequest(req)}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium">{req.employee_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{req.employee_department}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {isRTL ? typeLabel.ar : typeLabel.en}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm truncate max-w-[200px]" title={req.ai_summary}>
                          {req.ai_summary || req.original_message}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {req.original_language?.toUpperCase() || 'EN'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("flex items-center gap-1 w-fit", statusConfig.color)}>
                          <StatusIcon className="h-3 w-3" />
                          {isRTL ? statusConfig.label.ar : statusConfig.label.en}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {req.created_at ? format(new Date(req.created_at), 'MMM d, HH:mm') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-slate-500">
                {isRTL 
                  ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} من ${total}`
                  : `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} of ${total}`}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  {isRTL ? 'السابق' : 'Previous'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * pageSize >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  {isRTL ? 'التالي' : 'Next'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Request Detail Sheet */}
      <Sheet open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedRequest && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {isRTL 
                    ? REQUEST_TYPES[selectedRequest.request_type]?.ar 
                    : REQUEST_TYPES[selectedRequest.request_type]?.en}
                </SheetTitle>
                <SheetDescription>
                  {isRTL ? 'تفاصيل الطلب المنشأ بواسطة الذكاء الاصطناعي' : 'AI-created request details'}
                </SheetDescription>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Employee Info */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedRequest.employee_name}</p>
                    <p className="text-sm text-slate-500">{selectedRequest.employee_department}</p>
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">
                    {isRTL ? 'الحالة' : 'Status'}
                  </h4>
                  <Badge className={STATUS_CONFIG[selectedRequest.status]?.color}>
                    {isRTL 
                      ? STATUS_CONFIG[selectedRequest.status]?.label.ar 
                      : STATUS_CONFIG[selectedRequest.status]?.label.en}
                  </Badge>
                </div>
                
                {/* AI Summary */}
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">
                    {isRTL ? 'ملخص الذكاء الاصطناعي' : 'AI Summary'}
                  </h4>
                  <p className="text-sm bg-purple-50 p-3 rounded-lg border border-purple-100">
                    {selectedRequest.ai_summary}
                  </p>
                </div>
                
                {/* Original Message */}
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">
                    {isRTL ? 'الرسالة الأصلية' : 'Original Message'}
                  </h4>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg" dir={selectedRequest.original_language === 'ar' ? 'rtl' : 'ltr'}>
                    {selectedRequest.original_message}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {isRTL ? 'اللغة: ' : 'Language: '}{selectedRequest.original_language?.toUpperCase()}
                  </p>
                </div>
                
                {/* Action Area - Only for pending requests */}
                {selectedRequest.status === 'pending_hr' && (
                  <div className="border-t pt-6 space-y-4">
                    <h4 className="font-medium">
                      {isRTL ? 'اتخاذ إجراء' : 'Take Action'}
                    </h4>
                    
                    {/* Notes */}
                    <div>
                      <label className="text-sm text-slate-500">
                        {isRTL ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                      </label>
                      <Textarea
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        placeholder={isRTL ? 'أضف ملاحظات...' : 'Add notes...'}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Rejection Reason */}
                    <div>
                      <label className="text-sm text-slate-500">
                        {isRTL ? 'سبب الرفض (مطلوب للرفض)' : 'Rejection Reason (required for reject)'}
                      </label>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder={isRTL ? 'أدخل سبب الرفض...' : 'Enter rejection reason...'}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAction('approve')}
                        disabled={actionLoading}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {actionLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {isRTL ? 'موافقة' : 'Approve'}
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleAction('needs_info')}
                        disabled={actionLoading}
                        variant="outline"
                        className="flex-1"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        {isRTL ? 'طلب معلومات' : 'Need Info'}
                      </Button>
                      <Button
                        onClick={() => handleAction('reject')}
                        disabled={actionLoading}
                        variant="destructive"
                        className="flex-1"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {isRTL ? 'رفض' : 'Reject'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Existing HR notes */}
                {selectedRequest.hr_notes && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-2">
                      {isRTL ? 'ملاحظات الموارد البشرية' : 'HR Notes'}
                    </h4>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg">
                      {selectedRequest.hr_notes}
                    </p>
                  </div>
                )}
                
                {/* Rejection Reason */}
                {selectedRequest.hr_rejection_reason && (
                  <div>
                    <h4 className="text-sm font-medium text-red-500 mb-2">
                      {isRTL ? 'سبب الرفض' : 'Rejection Reason'}
                    </h4>
                    <p className="text-sm bg-red-50 p-3 rounded-lg text-red-700">
                      {selectedRequest.hr_rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AIInboxPage;
